import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import BottomNav from './BottomNav';
import SubscriptionLayer from './SubscriptionLayer';
import SupportChat from './SupportChat';
import MenuOnboardingTour from './MenuOnboardingTour';
import CreditsIndicator from './CreditsIndicator';
import WildQuestLogo from './WildQuestLogo';
import DailyIntroSting from './DailyIntroSting';
import LanguageSwitcher from './LanguageSwitcher';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../i18n/I18nContext';
import { Bell, Search, Menu, LogOut, Scale, Coins, Swords, Globe, BookOpen, HelpCircle, MessagesSquare } from 'lucide-react';
import { useEffect, useState, useCallback, useRef } from 'react';
import api from '../lib/api';
import { isAdminUser } from '../lib/routing';
import { replayMenuTour } from './MenuOnboardingTour';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

export default function AppShell() {
  const { user, logout } = useAuth();
  const t = useT();
  const nav = useNavigate();
  const location = useLocation();
  // Hide the BottomNav (with camera FAB) on the cinematic full-screen combat
  // scene so it doesn't overlay Live Arena controls or hit-block Arena CTAs.
  const isLiveBattle = /^\/battle\/live\//.test(location.pathname);
  const hideBottomNav = isLiveBattle;
  // Battle mode is truly full-screen — hide the top nav bar and outer padding
  // so the arena has the entire viewport for creature art and thumb reach.
  const hideTopChrome = isLiveBattle;
  const [summary, setSummary] = useState({ unread_notifications: 0, pending_challenges: 0, pending_duels: 0 });
  const [notes, setNotes] = useState([]);
  const [coins, setCoins] = useState(null);
  // Remember the highest duel count we've seen this session so the bell
  // pulses briefly when a new one arrives (delta > 0), then calms down.
  const [duelPulseKey, setDuelPulseKey] = useState(0);
  const prevDuelCountRef = useRef(0);

  const refresh = useCallback(() => {
    api.get('/notifications/summary').then(r => {
      const next = r.data;
      // Fire the pulse animation when the pending duel count went UP.
      if ((next.pending_duels || 0) > (prevDuelCountRef.current || 0)) {
        setDuelPulseKey(k => k + 1);
      }
      prevDuelCountRef.current = next.pending_duels || 0;
      setSummary(next);
    }).catch(() => {});
    api.get('/coins/balance').then(r => setCoins(r.data.wildcoins)).catch(() => {});
  }, []);
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [refresh]);

  const openBell = async () => {
    try {
      const { data } = await api.get('/notifications?limit=10');
      setNotes(data);
    } catch {}
  };

  const markAllRead = async () => {
    await api.post('/notifications/read-all').catch(() => {});
    setNotes((n) => n.map(x => ({ ...x, read: true })));
    setSummary(s => ({ ...s, unread_notifications: 0 }));
  };

  return (
    <div className={hideTopChrome ? "max-w-md mx-auto min-h-screen relative wq-landscape-shell" : "max-w-md mx-auto min-h-screen relative pb-32 wq-landscape-shell"}>
      <DailyIntroSting />
      {!hideTopChrome && (
      <header className="sticky top-0 z-40 glass border-b border-white/5 pt-[env(safe-area-inset-top)]">
        <div className="flex items-center justify-between px-3 sm:px-5 py-3 sm:py-4 gap-2">
          <button
            type="button"
            onClick={() => nav('/dashboard')}
            data-testid="header-brand"
            aria-label="WildQuest home"
            className="flex items-center gap-2 sm:gap-2.5 group min-w-0"
          >
            <WildQuestLogo className="w-8 h-8 sm:w-9 sm:h-9 transition-transform group-hover:scale-105 shrink-0" />
            <div className="text-left min-w-0">
              <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t('appShell.explorer', 'Explorer')}</p>
              <h1 className="font-serif text-lg sm:text-xl leading-none mt-0.5 sm:mt-1 truncate">{user?.name?.split(' ')[0] || 'Wildlife'}</h1>
            </div>
          </button>
          <div className="flex items-center gap-0.5 sm:gap-2 shrink-0">
            <div className="hidden sm:block"><CreditsIndicator /></div>
            {coins !== null && (
              <button
                type="button"
                data-testid="btn-wildcoins"
                onClick={() => nav('/marketplace')}
                className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent/10 border border-accent/30 hover:bg-accent/20 transition-colors"
                aria-label={t('appShell.wildcoinsAria', 'WildCoins balance')}
              >
                <Coins className="w-3.5 h-3.5 text-accent" strokeWidth={2} />
                <span data-testid="wildcoins-balance" className="text-xs font-mono tabular-nums text-accent">{coins}</span>
              </button>
            )}
            <button data-testid="btn-search" onClick={() => nav('/search')} className="hidden sm:inline-flex p-1.5 sm:p-2 rounded-full hover:bg-white/5 transition-colors" aria-label={t('appShell.search', 'Search')}><Search className="w-5 h-5 text-muted-foreground" strokeWidth={1.75} /></button>

            {/* Language switcher — hidden on mobile (accessible via menu) */}
            <div className="hidden sm:block"><LanguageSwitcher compact /></div>

            {/* Challenges badge */}
            {summary.pending_challenges > 0 && (
              <button data-testid="btn-challenges-badge" onClick={() => nav('/challenges')} className="relative p-1.5 sm:p-2 rounded-full hover:bg-white/5" aria-label="Pending challenges">
                <Scale className="w-5 h-5 text-muted-foreground" strokeWidth={1.75} />
                <span data-testid="challenges-badge-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-accent text-black text-[10px] font-mono font-semibold flex items-center justify-center">{summary.pending_challenges}</span>
              </button>
            )}

            {/* Duel Inbox Bell — pulses briefly whenever a new challenge
                lands so the recipient sees it without hunting for
                /battle/duels. Only rendered when at least one duel is
                awaiting the caller's accept/decline. */}
            {summary.pending_duels > 0 && (
              <button
                type="button"
                onClick={() => nav('/battle/duels')}
                data-testid="btn-duel-inbox"
                key={`duel-bell-${duelPulseKey}`}
                className="relative p-1.5 sm:p-2 rounded-full hover:bg-white/5 transition-colors animate-[wq-shake_0.6s_ease-in-out] wq-duel-bell"
                aria-label={t('appShell.duelInbox', `${summary.pending_duels} pending duel${summary.pending_duels > 1 ? 's' : ''}`)}
              >
                {/* Radiant halo ping */}
                <span className="absolute inset-0 rounded-full ring-2 ring-rose-400/70 animate-ping opacity-70 pointer-events-none" />
                <Swords className="w-5 h-5 text-rose-300" strokeWidth={2} />
                <span
                  data-testid="duel-badge-count"
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-gradient-to-r from-rose-500 to-orange-500 text-black text-[10px] font-mono font-bold flex items-center justify-center border border-black/40"
                >
                  {summary.pending_duels}
                </span>
              </button>
            )}

            {/* Notifications bell */}
            <Popover onOpenChange={(o) => o && openBell()}>
              <PopoverTrigger asChild>
                <button data-testid="btn-notifications" className="relative p-1.5 sm:p-2 rounded-full hover:bg-white/5 transition-colors" aria-label={t('appShell.notifications', 'Notifications')}>
                  <Bell className="w-5 h-5 text-muted-foreground" strokeWidth={1.75} />
                  {summary.unread_notifications > 0 && (
                    <span data-testid="notifications-badge-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-white text-[10px] font-mono font-semibold flex items-center justify-center">{summary.unread_notifications}</span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 bg-card border-white/10 p-0">
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t('appShell.notifications', 'Notifications')}</p>
                  {notes.some(n => !n.read) && (
                    <button data-testid="btn-mark-all-read" onClick={markAllRead} className="text-[10px] uppercase tracking-widest text-accent">{t('appShell.markAllRead', 'Mark all read')}</button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notes.length === 0 ? (
                    <div className="p-6 text-center text-xs text-muted-foreground">{t('appShell.noNotifications', 'No notifications yet.')}</div>
                  ) : notes.map(n => (
                    <button
                      key={n.notification_id}
                      data-testid="notification-item"
                      onClick={async () => {
                        await api.post(`/notifications/${n.notification_id}/read`).catch(() => {});
                        if (n.link) nav(n.link);
                      }}
                      className={`w-full text-left px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/5 ${!n.read ? 'bg-primary/5' : ''}`}
                    >
                      <div className="flex items-start gap-2">
                        {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm">{n.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>
                          <p className="text-[10px] font-mono text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="btn-settings" className="p-1.5 sm:p-2 rounded-full hover:bg-white/5 transition-colors" aria-label={t('appShell.menu.title', 'Menu')}>
                  <Menu className="w-6 h-6 sm:w-5 sm:h-5 text-white" strokeWidth={2} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-card border-white/10 max-h-[80vh] overflow-y-auto">
                {/* Mobile-only quick actions (hidden on ≥sm because they live in the header there) */}
                <DropdownMenuItem data-testid="menu-search-mobile" className="sm:hidden" onClick={() => nav('/search')}>
                  <Search className="w-4 h-4 mr-2" />{t('appShell.search', 'Search')}
                </DropdownMenuItem>
                {coins !== null && (
                  <DropdownMenuItem data-testid="menu-wildcoins-mobile" className="sm:hidden" onClick={() => nav('/marketplace')}>
                    <Coins className="w-4 h-4 mr-2 text-accent" />
                    <span>{t('appShell.menu.wildcoins', 'WildCoins')}</span>
                    <span className="ml-auto text-[10px] font-mono tabular-nums text-accent">{coins}</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-white/10 sm:hidden" />
                <DropdownMenuItem data-testid="menu-profile" onClick={() => nav('/profile')}>{t('appShell.menu.profile', 'Profile')}</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-leaderboard" onClick={() => nav('/leaderboard')}>{t('appShell.menu.leaderboard', 'Leaderboard')}</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-quests" onClick={() => nav('/quests')}>{t('appShell.menu.quests', 'Quests')}</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-achievements" onClick={() => nav('/achievements')}>{t('appShell.menu.achievements', 'Achievements')}</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-vault" onClick={() => nav('/vault')}>{t('appShell.menu.vault', 'Battle Vault')}</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-arena" onClick={() => nav('/battle')}>{t('appShell.menu.arena', 'Battle Arena')}</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-collections" onClick={() => nav('/collections')}>{t('appShell.menu.collections', 'Collections')}</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-membership" onClick={() => nav('/membership')}>{t('appShell.menu.membership', 'Membership & Credits')}</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-duels" onClick={() => nav('/battle/duels')}>{t('appShell.menu.duels', 'Friend Duels')}</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-marketplace" onClick={() => nav('/marketplace')}>{t('appShell.menu.marketplace', 'Marketplace')}</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-alchemy" onClick={() => nav('/alchemy')}>{t('appShell.menu.alchemy', 'Alchemy Bench')}</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-records" onClick={() => nav('/records')}>{t('appShell.menu.records', 'Discovery Records')}</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-stats" onClick={() => nav('/stats')}>{t('appShell.menu.stats', 'Statistics')}</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-regions" onClick={() => nav('/regions')}>{t('appShell.menu.regions', 'Regions')}</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-friends" onClick={() => nav('/friends')}>{t('appShell.menu.friends', 'Friends')}</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-challenges" onClick={() => nav('/challenges')}>
                  {t('appShell.menu.challenges', 'Challenges')}{summary.pending_challenges > 0 && <span className="ml-auto text-[10px] font-mono text-accent">{summary.pending_challenges}</span>}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="menu-bible" onClick={() => nav('/bible')}>
                  <BookOpen className="w-4 h-4 mr-2 text-emerald-300" />
                  {t('appShell.menu.bible', 'Game Bible')}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-lodge" onClick={() => nav('/lodge')}>
                  <MessagesSquare className="w-4 h-4 mr-2 text-emerald-300" />
                  {t('appShell.menu.lodge', "Explorer's Lodge")}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-replay-tour" onClick={() => replayMenuTour()}>
                  <HelpCircle className="w-4 h-4 mr-2 text-emerald-300" />
                  {t('appShell.menu.replayTour', 'Show tips again')}
                </DropdownMenuItem>
                {isAdminUser(user) && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => nav('/admin')}>{t('appShell.menu.admin', 'Admin')}</DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-white/10" />
                {/* Language switcher — mobile-only (accessible via inline dropdown on ≥sm) */}
                <div className="sm:hidden px-2 py-2 flex items-center gap-2" data-testid="menu-language-mobile">
                  <Globe className="w-4 h-4 text-muted-foreground" />
                  <div className="flex-1"><LanguageSwitcher compact align="right" /></div>
                </div>
                <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="text-destructive"><LogOut className="w-4 h-4 mr-2" />{t('appShell.menu.logout', 'Logout')}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      )}

      <main className={hideTopChrome ? "" : "px-5 pt-6"}><Outlet /></main>
      {!hideBottomNav && <BottomNav />}
      <SubscriptionLayer />
      {!hideBottomNav && <SupportChat />}
      {!hideTopChrome && <MenuOnboardingTour />}
    </div>
  );
}
